import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Factory,
  ClipboardList,
  Boxes,
  Users,
  Truck,
  UserPlus,
  Wallet,
  Settings as SettingsIcon,
  LogOut,
  CircleUser,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBusinessUnit, BUSINESS_UNITS } from "@/context/BusinessUnitContext";
import { COMPANY_NAME, COMPANY_SHORT, COMPANY_TAGLINE, COMPANY_INITIALS } from "@/lib/brand";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "production", "sales", "dispatch"] },
  { to: "/orders", label: "Orders", icon: ClipboardList, roles: ["admin", "sales", "production", "dispatch"] },
  { to: "/inventory", label: "Inventory", icon: Boxes, roles: ["admin", "production", "sales"] },
  { to: "/shift-stock", label: "Shift Stock", icon: Factory, roles: ["admin", "production", "dispatch"] },
  { to: "/customers", label: "Customers", icon: Users, roles: ["admin", "sales", "account"] },
  { to: "/payments", label: "Payments", icon: Wallet, roles: ["admin", "account", "sales"] },
  { to: "/inquiries", label: "New Inquiry", icon: UserPlus, roles: ["admin", "sales"] },
  { to: "/suppliers", label: "Suppliers", icon: Truck, roles: ["admin", "production"] },
  { to: "/settings", label: "Settings", icon: SettingsIcon, roles: ["admin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { businessUnit, meta, setBusinessUnit } = useBusinessUnit();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [buMenuOpen, setBuMenuOpen] = useState(false);

  const items = NAV.filter((n) => !user || n.roles.includes(user.role));

  // Close drawer whenever the route changes (mobile UX).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open on mobile.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  return (
    <div className="min-h-screen flex bg-zinc-100 overflow-x-hidden">
      {/* Mobile backdrop */}
      {drawerOpen && (
        <button
          aria-label="Close menu"
          data-testid="sidebar-backdrop"
          onClick={() => setDrawerOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 z-40"
        />
      )}

      {/* Sidebar (drawer on mobile, fixed on desktop) */}
      <aside
        className={`w-60 bg-zinc-950 text-zinc-300 flex flex-col fixed left-0 top-0 bottom-0 border-r border-zinc-800 z-50 transform transition-transform duration-200 md:translate-x-0 ${drawerOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <Link
          to="/dashboard"
          className="px-5 py-5 border-b border-zinc-800 block"
          style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}
          data-testid="brand-link"
        >
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 bg-orange-600 flex items-center justify-center text-white font-mono font-bold text-sm tracking-tight">
              {COMPANY_INITIALS}
            </div>
            <div className="min-w-0">
              <div className="text-white font-semibold tracking-tight text-[13px] leading-tight truncate">{COMPANY_SHORT}</div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 leading-tight mt-0.5">
                {COMPANY_TAGLINE}
              </div>
            </div>
          </div>
        </Link>

        <nav className="flex-1 py-4 overflow-y-auto">
          <div className="px-5 pb-2 text-[10px] font-mono uppercase tracking-widest text-zinc-600">
            Operations
          </div>
          {items.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors duration-75 border-l-2 ${
                    isActive
                      ? "bg-zinc-900 text-white border-orange-600"
                      : "border-transparent hover:bg-zinc-900 hover:text-white"
                  }`
                }
              >
                <Icon size={16} strokeWidth={2} />
                <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div
          className="border-t border-zinc-800 px-5 py-4"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <div className="flex items-center gap-2 mb-3">
            <CircleUser size={20} className="text-zinc-400" />
            <div className="min-w-0 flex-1">
              <div className="text-xs text-white truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-500" data-testid="user-role">
                {user?.role}
              </div>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 text-zinc-300 hover:text-white px-3 py-2 text-xs uppercase font-mono tracking-wider transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="w-full md:ml-60 md:w-[calc(100%-15rem)] flex-1 min-h-screen relative">
        <header className="bg-white border-b border-zinc-300 px-3 md:px-8 py-3 flex items-center justify-between relative z-10 gap-3">
          <button
            type="button"
            data-testid="open-sidebar-btn"
            onClick={() => setDrawerOpen(true)}
            className="md:hidden h-9 w-9 flex items-center justify-center border border-zinc-300 bg-white text-zinc-800 hover:bg-zinc-50 shrink-0"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 truncate flex-1 min-w-0">
            {location.pathname.replace("/", "") || "dashboard"}
          </div>
          <div className="flex items-center gap-4 shrink-0">
            {/* Vertical (Business Unit) switcher — a small pill in the header */}
            {meta && (
              <div className="relative">
                <button
                  data-testid="bu-switcher-btn"
                  onClick={() => setBuMenuOpen((v) => !v)}
                  className="inline-flex items-center gap-2 px-2 py-1 border font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-50"
                  style={{ borderColor: meta.accent, color: meta.accent }}
                  title={`Current: ${meta.label} — click to switch`}
                >
                  <span className="inline-block h-2 w-2" style={{ background: meta.accent }} />
                  {meta.short}
                  <span className="text-zinc-400">▾</span>
                </button>
                {buMenuOpen && (
                  <>
                    <button
                      aria-label="Close menu"
                      onClick={() => setBuMenuOpen(false)}
                      className="fixed inset-0 z-30 cursor-default"
                    />
                    <div className="absolute right-0 top-full mt-1 z-40 w-64 bg-white border border-zinc-300 shadow-lg" data-testid="bu-switcher-menu">
                      <div className="px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                        Switch business unit
                      </div>
                      {BUSINESS_UNITS.map((bu) => {
                        const active = bu.key === businessUnit;
                        return (
                          <button
                            key={bu.key}
                            data-testid={`bu-option-${bu.key}`}
                            onClick={() => {
                              setBusinessUnit(bu.key);
                              setBuMenuOpen(false);
                              // Full reload so cached data on every page refetches under the new BU header.
                              window.location.reload();
                            }}
                            className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-orange-50 transition-colors ${active ? "bg-zinc-50" : ""}`}
                          >
                            <span className="mt-1 inline-block h-2 w-2 shrink-0" style={{ background: bu.accent }} />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-semibold text-zinc-950">{bu.label}</div>
                              <div className="text-[10px] font-mono text-zinc-500">{bu.tagline}</div>
                            </div>
                            {active && <span className="text-[9px] font-mono uppercase text-orange-700">Active</span>}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="hidden md:block font-mono text-[10px] uppercase tracking-widest text-zinc-700 border-l border-zinc-300 pl-4" data-testid="header-company">
              <span className="text-orange-600">●</span> {COMPANY_NAME}
            </div>
            <div className="hidden sm:block font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid="header-time">
              {new Date().toLocaleString("en-IN", { hour12: false })}
            </div>
          </div>
          {drawerOpen && (
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close menu"
              className="md:hidden h-9 w-9 flex items-center justify-center border border-zinc-300 bg-white text-zinc-800"
            >
              <X size={18} />
            </button>
          )}
        </header>

        {/* Diagonal text watermark fixed behind content */}
        <div className="text-watermark" aria-hidden>
          <span>
            {COMPANY_SHORT}
            <small>· {COMPANY_TAGLINE} ·</small>
          </span>
        </div>

        <div className="p-3 sm:p-5 md:p-8 bg-grid min-h-[calc(100vh-49px)] relative overflow-x-hidden">
          <Outlet />
        </div>

        {/* Corner badge watermark (desktop only — clutters small screens) */}
        <div className="hidden md:flex corner-watermark" aria-hidden data-testid="corner-watermark">
          <div className="mark">{COMPANY_INITIALS}</div>
          <div className="label">
            <strong>{COMPANY_SHORT}</strong>
            <span>StretchOps · v1.0</span>
          </div>
        </div>
      </main>
    </div>
  );
}
